import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '../components/Icon'
import { Legend } from '../components/misc'
import { RowSkeleton } from '../components/Skeleton'
import { AttendantSelect } from '../components/AttendantSelect'
import { Select } from '../components/Select'
import { PresenceDot } from '../components/PresenceDot'
import { PriorityFlag } from '../components/badges'
import { isOverdueRow, isScheduledRow, isWaitingForCall } from './operatorPanel'
import { useTerms } from '../i18n/TermsContext'
import { useReference } from '../data/ReferenceContext'
import { actionsFor, isPending, elapsedUrgency } from '../domain/engine'
import { queueSemaphore, queueSemaphoreOrder } from '../domain/queueSemaphore'
import { useTick } from '../components/TickContext'
import type { Attendant, OperatorRow, OperatorWork, PresenceState, QueueWait, ServiceConfig, Session } from '../domain/types'
import type { OperatorWorkPeriod } from '../data/DataSource'
import type { AppActions } from '../store/types'
import { env } from '../config/env'
import { setTags } from '../instrument'

const PERIOD_OPTIONS: { value: OperatorWorkPeriod; label: string }[] = [
  { value: 'morning', label: 'Manhã' },
  { value: 'afternoon', label: 'Tarde' },
  { value: 'night', label: 'Noite' },
]

// Extrai a hora (0-23) do início de uma sessão pra classificar o período — mesma regra do backend
// (MatchesPeriod: manhã <12h, tarde 12-18h, noite >=18h). Aceita "HH:mm..." ou "...THH:mm...".
function sessionHour(session: { start?: string }): number | null {
  if (!session.start) {return null}
  const match = session.start.match(/T?(\d{2}):\d{2}/)
  return match ? Number(match[1]) : null
}

function matchesPeriod(session: { start?: string }, period: OperatorWorkPeriod | null): boolean {
  if (!period) {return true}
  const hour = sessionHour(session)
  if (hour == null) {return true}
  if (period === 'morning') {return hour < 12}
  if (period === 'afternoon') {return hour >= 12 && hour < 18}
  return hour >= 18
}

function isToday(dateString?: string | null): boolean {
  if (!dateString) {return false}
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {return false}
  const today = new Date()
  return date.toDateString() === today.toDateString()
}

function isScheduledForToday(sessionStart?: string | null): boolean {
  return isToday(sessionStart)
}

function sessionStartMs(row: OperatorRow): number {
  const ms = row.t.sessionStart ? Date.parse(row.t.sessionStart) : NaN
  return Number.isNaN(ms) ? Infinity : ms
}

function sessionOptionLabel(session: Session, count: number): string {
  const range = session.start && session.end ? ` · ${session.start.slice(0, 5)}-${session.end.slice(0, 5)}` : ''
  return `${session.label}${range} (${count} na fila)`
}

// "há X min" desde a última chamada (lastTimeCalled é "HH:MM" local, como nowHHMM).
// Retorna null quando não dá pra comparar (meia-noite atravessada, formato inesperado).
function minutesSinceHHMM(hhmm?: string | null): number | null {
  if (!hhmm) {return null}
  const m = hhmm.match(/^(\d{2}):(\d{2})/)
  if (!m) {return null}
  const now = new Date()
  const diff = now.getHours() * 60 + now.getMinutes() - (Number(m[1]) * 60 + Number(m[2]))
  if (diff < 0 || diff >= 1440) {return null}
  return diff
}

function calledWaitLabel(mins: number | null): string | null {
  if (mins == null) {return null}
  if (mins < 1) {return 'agora'}
  if (mins === 1) {return 'há 1 min'}
  if (mins < 60) {return `há ${mins} min`}
  const h = Math.floor(mins / 60)
  const mm = mins % 60
  return mm ? `há ${h}h${String(mm).padStart(2, '0')}` : `há ${h}h`
}

const ServingCard = memo(function ServingCard({ row, actions, actingResourceId }: { row: OperatorRow; actions: AppActions; actingResourceId: string | null }) {
  const { metaSummary } = useReference()
  const { t, a } = row
  const acts = actionsFor(a.tickets, t)
  const starting = actions.isTicketActionPending?.(t.ticketId, 'start') ?? false
  const recalling = actions.isTicketActionPending?.(t.ticketId, 'recall') ?? false
  const reopening = actions.isTicketActionPending?.(t.ticketId, 'reopen') ?? false
  const reinserting = actions.isTicketActionPending?.(t.ticketId, 'reinsert') ?? false
  const completing = actions.isTicketActionPending?.(t.ticketId, 'complete') ?? false
  const transferring = actions.isTicketActionPending?.(t.ticketId, 'transfer') ?? false
  const sessionTime = sessionTimeRange(t.sessionStart, t.sessionEnd)

  // Re-render a cada 15s para atualizar o cronômetro de minutos decorridos.
  // Antes: cada ticket "Started" mantinha seu próprio setInterval(15s). N
  // tickets = N timers paralelos. Agora: 1 timer global via TickProvider.
  // `useTick` retorna Date.now() atualizado a cada tick; `elapsedUrgency` lê
  // Date.now() internamente, então o re-render refresca a urgência.
  useTick()

  const isCalled = t.status === 'Called'
  const isOtherOperator = isCalled && t.resourceId !== actingResourceId
  const urg = isCalled ? null : elapsedUrgency(t._startedAt, t.estMin)
  // meta numa linha só: o que identifica o atendimento, sem repetir o que já está no topo
  const meta = [sessionTime, t.nomeServico, t.sala, ...metaSummary(a.metadata)].filter(Boolean).join(' · ')
  // Estado "Called": espera ativa — há quanto tempo a última chamada ficou sem resposta.
  const waited = isCalled ? calledWaitLabel(minutesSinceHHMM(t.lastTimeCalled)) : null
  return (
    <div
      className={'fz-work ' + (isCalled ? 'fz-work--called' : 'fz-work--serving')}
      style={{ viewTransitionName: `fzticket-${t.ticketId}` } as React.CSSProperties}
      data-screen-label={`Em atendimento ${t.codigoTicket}`}
    >
      <div className="fz-work__body">
        <div className="fz-work__head">
          <span className="fz-work__code">{t.codigoTicket}</span>
          <span className="fz-work__name">{a.cliente.nome}</span>
          <PriorityFlag prioridade={a.prioridade} />
          {isCalled
            ? <span
                className={'fz-work__tag' + ((t.timesCalled ?? 0) >= 3 ? ' fz-work__tag--stale' : '')}
                title={t.lastTimeCalled
                  ? `Última chamada às ${t.lastTimeCalled}${(t.timesCalled ?? 0) >= 3 ? ' — sem resposta: mover para agendados ou reinserir no final da fila' : ''}`
                  : 'Aguardando chegar'}
              ><Icon name="megaphone" size={11} /> Chamado{t.timesCalled && t.timesCalled > 1 ? ` ${t.timesCalled}×` : ''}{waited ? ` · ${waited}` : ''}{isOtherOperator ? ' · outro operador' : ''}</span>
            : <span className={'fz-work__timer' + (urg === 'danger' ? ' is-danger' : urg === 'warning' ? ' is-warning' : '')}>
                <Icon name={urg === 'neutral' ? 'clock' : 'alert'} size={11} /> {actions.elapsedOf(t)}
              </span>}
        </div>
        <div className="fz-work__meta">
          {meta && <span>{meta}</span>}
          {t.resourceNome && <span><Icon name="user" size={11} /> {t.resourceNome}</span>}
        </div>
      </div>
      <div className="fz-work__actions">
        {acts.has('recall') && <button className="fz-iconbtn fz-iconbtn--sm" title="Rechamar" aria-label="Rechamar" disabled={recalling} aria-busy={recalling} onClick={() => actions.call(t.ticketId, true, t.resourceId)}>{recalling ? <span className="fz-spin" /> : <Icon name="megaphone" size={14} />}</button>}
        {acts.has('reopen') && <button className="fz-iconbtn fz-iconbtn--sm" title="Mover para agendados" aria-label="Mover para agendados" disabled={reopening} aria-busy={reopening} onClick={() => actions.reopen(t.ticketId)}>{reopening ? <span className="fz-spin" /> : <Icon name="calendar" size={14} />}</button>}
        {acts.has('reinsert') && <button className="fz-iconbtn fz-iconbtn--sm" title="Reinserir no final da fila" aria-label="Reinserir no final da fila" disabled={reinserting} aria-busy={reinserting} onClick={() => actions.reinsert(t.ticketId)}>{reinserting ? <span className="fz-spin" /> : <Icon name="refresh" size={14} />}</button>}
        {acts.has('transfer') && <button className="fz-iconbtn fz-iconbtn--sm" title="Transferir" aria-label="Transferir" disabled={transferring} aria-busy={transferring} onPointerEnter={() => actions.warmFields?.(t.ticketId, 'OnTransfer')} onFocus={() => actions.warmFields?.(t.ticketId, 'OnTransfer')} onClick={() => actions.openTransfer(t.ticketId)}>{transferring ? <span className="fz-spin" /> : <Icon name="arrowLeftRight" size={14} />}</button>}
        <button className="fz-iconbtn fz-iconbtn--sm" title="Detalhes" aria-label="Detalhes" onClick={() => actions.openDetails(t.ticketId)}><Icon name="info" size={14} /></button>
        {acts.has('start') && <button className="fz-btn fz-btn--primary fz-btn--sm" disabled={starting} aria-busy={starting} onClick={() => actions.start(t.ticketId, t.resourceId)}>{starting ? <span className="fz-spin" /> : <Icon name="play" size={14} />} {starting ? 'Iniciando…' : 'Iniciar'}</button>}
        {acts.has('complete') && <button className="fz-btn fz-btn--primary fz-btn--sm" disabled={completing} aria-busy={completing} onPointerEnter={() => actions.warmFields?.(t.ticketId, 'OnComplete')} onFocus={() => actions.warmFields?.(t.ticketId, 'OnComplete')} onClick={() => actions.complete(t.ticketId)}>{completing ? <span className="fz-spin" /> : <Icon name="check" size={14} />} {completing ? 'Concluindo…' : 'Concluir'}</button>}
        {acts.has('complete') && a.aggregatorId && <button className="fz-work__complete-edit" title="Adicionar etapa e concluir ao salvar" aria-label="Adicionar etapa e concluir ao salvar" disabled={completing} aria-busy={completing} onClick={() => actions.completeAndAddStep(t.ticketId)}>{completing ? <span className="fz-spin" /> : <Icon name="pencil" size={14} />} {completing ? 'Concluindo…' : 'Concluir e editar'}</button>}
      </div>
    </div>
  )
})

function sessionTimeRange(sessionStart?: string | null, sessionEnd?: string | null): string | null {
  if (!sessionStart) {return null}
  const startDate = new Date(sessionStart)
  const endDate = sessionEnd ? new Date(sessionEnd) : null
  if (isNaN(startDate.getTime())) {return null}
  // Converte UTC para UTC-3 (São Paulo)
  startDate.setHours(startDate.getHours() - 3)
  const startHour = String(startDate.getHours()).padStart(2, '0')
  const startMin = String(startDate.getMinutes()).padStart(2, '0')
  if (!endDate || isNaN(endDate.getTime())) {return `${startHour}:${startMin}`}
  endDate.setHours(endDate.getHours() - 3)
  const endHour = String(endDate.getHours()).padStart(2, '0')
  const endMin = String(endDate.getMinutes()).padStart(2, '0')
  return `${startHour}:${startMin}–${endHour}:${endMin}`
}

const EligCard = memo(function EligCard({ row, i, actions, presenceOf }: { row: OperatorRow; i: number; actions: AppActions; presenceOf: (id: string | number) => PresenceState }) {
  const { metaSummary } = useReference()
  const { t, a } = row
  const acts = actionsFor(a.tickets, t)
  const pres = presenceOf(t.ticketId ?? a.aggregatorId)
  const callingTicket = actions.isTicketActionPending?.(t.ticketId, 'call') ?? false
  const isLate = !env.singleDay && (t.isOverdue || t.scheduleState === 'Expired') && isScheduledForToday(t.sessionStart)
  const sessionTime = sessionTimeRange(t.sessionStart, t.sessionEnd)
  return (
    <div className="fz-elig" style={{ '--i': Math.min(i, 5), viewTransitionName: `fzticket-${t.ticketId}` } as React.CSSProperties} data-screen-label={`Elegível ${t.codigoTicket}`}>
      <div className="fz-elig__pos">{i + 1}</div>
      <div className="fz-elig__body">
        <div className="fz-elig__name">
          <span className="fz-work__code">{t.codigoTicket}</span>
          <span>{a.cliente.nome}</span>
          <PriorityFlag prioridade={a.prioridade} />
          {/* o PresenceDot já diz "Offline"/"Ausente" — não repetimos "fora do lobby" no meta */}
          <PresenceDot state={pres} />
        </div>
        <div className="fz-elig__meta">
          <span>{[sessionTime, [t.nomeServico, t.sala].filter(Boolean).join(' · '), metaSummary(a.metadata)[0]].filter(Boolean).join(' · ')}</span>
          {isLate && <span style={{ color: 'var(--color-warning-deep)' }}><Icon name="alert" size={12} /> Atrasado</span>}
          {!isLate && t.estMin > 0 && <span><Icon name="clock" size={12} /> ~{t.estMin} min</span>}
        </div>
      </div>
      <div className="fz-work__actions">
        <button className="fz-iconbtn fz-iconbtn--sm" title="Detalhes" aria-label="Ver detalhes" onClick={() => actions.openDetails(t.ticketId)}><Icon name="info" size={14} /></button>
        {acts.has('call') && <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={callingTicket} aria-busy={callingTicket} onClick={() => actions.call(t.ticketId, false, t.resourceId)}>{callingTicket ? <span className="fz-spin" /> : <Icon name="megaphone" size={14} />} {callingTicket ? 'Chamando este…' : 'Chamar este'}</button>}
      </div>
    </div>
  )
})

const PreCheckinCard = memo(function PreCheckinCard({ row, i, actions, presenceOf }: { row: OperatorRow; i: number; actions: AppActions; presenceOf: (id: string | number) => PresenceState }) {
  const { metaSummary } = useReference()
  const { t, a } = row
  const pres = presenceOf(t.ticketId ?? a.aggregatorId)
  const scheduled = isScheduledRow(row)
  const action = scheduled ? 'confirm' : 'check'
  const actionLabel = scheduled ? (t.status === 'Reopened' ? 'Confirmar novamente' : 'Confirmar') : 'Check-in'
  const checking = actions.isTicketActionPending?.(t.ticketId, action) ?? false
  const sessionTime = sessionTimeRange(t.sessionStart, t.sessionEnd)
  return (
    <div className="fz-elig" style={{ '--i': Math.min(i, 5), viewTransitionName: `fzticket-${t.ticketId}` } as React.CSSProperties} data-screen-label={`${scheduled ? 'Agendado' : 'Confirmado'} ${t.codigoTicket}`}>
      <div className="fz-elig__pos">{i + 1}</div>
      <div className="fz-elig__body">
        <div className="fz-elig__name">
          <span className="fz-work__code">{t.codigoTicket}</span>
          <span>{a.cliente.nome}</span>
          <PriorityFlag prioridade={a.prioridade} />
          <PresenceDot state={pres} />
        </div>
        <div className="fz-elig__meta">
          <span>{[sessionTime, [t.nomeServico, t.sala].filter(Boolean).join(' · '), metaSummary(a.metadata)[0]].filter(Boolean).join(' · ')}</span>
        </div>
      </div>
      <div className="fz-work__actions">
        <button className="fz-iconbtn fz-iconbtn--sm" title="Detalhes" aria-label="Ver detalhes" onClick={() => actions.openDetails(t.ticketId)}><Icon name="info" size={14} /></button>
        <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={checking} aria-busy={checking} onClick={() => actions[action](t.ticketId)}>
          {checking ? <span className="fz-spin" /> : <Icon name={scheduled ? 'check' : 'pin'} size={14} />} {checking ? (scheduled ? 'Confirmando…' : 'Fazendo check-in…') : actionLabel}
        </button>
      </div>
    </div>
  )
})

const BlockedCard = memo(function BlockedCard({ row, actions }: { row: OperatorRow; actions: AppActions }) {
  const { t, a, info, depNames } = row
  const acts = actionsFor(a.tickets, t)
  const confirming = actions.isTicketActionPending?.(t.ticketId, 'confirm') ?? false
  const checking = actions.isTicketActionPending?.(t.ticketId, 'check') ?? false
  const rescheduling = actions.isTicketActionPending?.(t.ticketId, 'reschedule') ?? false
  const calling = actions.isTicketActionPending?.(t.ticketId, 'call') ?? false
  const deps = depNames ?? []
  const isOverdue = t.isOverdue || t.scheduleState === 'Expired'
  const overdueToday = isOverdue && isScheduledForToday(t.sessionStart)
  const sessionTime = sessionTimeRange(t.sessionStart, t.sessionEnd)
  const reason = isOverdue ? 'Sessão vencida'
    : info.razao === 'concorrente' ? 'Outro serviço do nível em atendimento' : 'Aguardando etapa anterior'
  const depSummary = deps.map((d) => `${d.code} · ${d.nome}`).join('; ')
  return (
    <div className="fz-elig fz-elig--blocked" style={{ viewTransitionName: `fzticket-${t.ticketId}` } as React.CSSProperties}>
      <div className="fz-elig__pos"><Icon name="lock" size={16} /></div>
      <div className="fz-elig__body">
        <div className="fz-elig__name">
          <span className="fz-work__code">{t.codigoTicket}</span>
          <span>{a.cliente.nome}</span>
          <PriorityFlag prioridade={a.prioridade} />
        </div>
        <div className="fz-elig__meta">
          <span>{[sessionTime, t.nomeServico].filter(Boolean).join(' · ')}</span>
          <details className="fz-blocked-details">
            <summary className="fz-elig__blocked-reason" aria-label="Por que está bloqueado">
              <Icon name="lock" size={12} /> {reason}{depSummary ? ` · ${depSummary}` : ''}
            </summary>
            <div className="fz-blocked-details__content">
              <strong>Bloqueado</strong>{info.motivo ?? reason}
              {deps.length > 0 && <div className="fz-tip__deps">Depende de: {deps.map((d, i) => <span key={i}><code>{d.code}</code> · {d.nome}{i < deps.length - 1 ? '; ' : ''}</span>)}</div>}
            </div>
          </details>
        </div>
      </div>
      <div className="fz-work__actions">
        {acts.has('confirm') && (
          <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={confirming} aria-busy={confirming} onPointerEnter={() => actions.warmFields?.(t.ticketId, 'OnConfirm')} onFocus={() => actions.warmFields?.(t.ticketId, 'OnConfirm')} onClick={() => actions.confirm(t.ticketId)}>
            {confirming ? <span className="fz-spin" /> : <Icon name="check" size={14} />} {confirming ? 'Confirmando…' : 'Confirmar novamente'}
          </button>
        )}
        {acts.has('check') && (
          <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={checking} aria-busy={checking} onClick={() => actions.check(t.ticketId)}>
            {checking ? <span className="fz-spin" /> : <Icon name="pin" size={14} />} {checking ? 'Fazendo check-in…' : 'Check-in'}
          </button>
        )}
        {overdueToday && (
          <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={calling} aria-busy={calling} onClick={() => actions.call(t.ticketId, false, t.resourceId)}>
            {calling ? <span className="fz-spin" /> : <Icon name="megaphone" size={14} />} {calling ? 'Chamando…' : 'Chamar'}
          </button>
        )}
        {acts.has('reschedule') && (
          <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={rescheduling} aria-busy={rescheduling} onClick={() => actions.openReschedule(t.ticketId)}>
            {rescheduling ? <span className="fz-spin" /> : <Icon name="clock" size={14} />} {rescheduling ? 'Reagendando…' : 'Reagendar'}
          </button>
        )}
        {!acts.has('confirm') && !acts.has('check') && !acts.has('reschedule') && !overdueToday && (
          <span className="fz-status fz-status--locked fz-status--sm" data-tid={t.ticketId}><Icon name="lock" size={11} /> Bloqueado</span>
        )}
      </div>
    </div>
  )
})

export function OperadorScreen({
  work, loading = false, selServiceConfigId, onSelServiceConfig, selSessionId, onSelSession, selPeriod, onSelPeriod, opLayout, actions, calling, opCounts,
  serviceConfigs: visibleServiceConfigs, queueWaits, semaphoreLimits, attendants, actingResourceId, onActingResource, filterResourceId, onFilterResource, presenceOf, presenceOfWatch,
}: {
  work: OperatorWork | null
  loading?: boolean
  selServiceConfigId: number
  onSelServiceConfig: (id: number) => void
  selSessionId: number | null
  onSelSession: (id: number | null) => void
  selPeriod: OperatorWorkPeriod | null
  onSelPeriod: (period: OperatorWorkPeriod | null) => void
  opLayout: string
  actions: AppActions
  calling: null | 'next' | 'pref'
  serviceConfigs?: ServiceConfig[]
  queueWaits?: QueueWait[]
  semaphoreLimits?: { yellow: number; red: number } | null
  attendants: Attendant[]
  actingResourceId: string | null
  onActingResource: (id: string | null) => void
  filterResourceId: string | null
  onFilterResource: (id: string | null) => void
  presenceOf: (ticketIdOrAggId: string | number) => PresenceState
  presenceOfWatch?: (scid: number) => void
  opCounts?: Pick<OperatorWork, 'eligCountByServiceConfig' | 'eligCountBySession' | 'otherCountByServiceConfig'> | null
}) {
  const terms = useTerms()
  const { serviceConfigs, serviceConfigById, sessionsOf } = useReference()
  // presença: o App passa `presenceOf={(id) => rt.presenceOf(id)}` (nova identidade por render).
  // Ref + useCallback dão identidade estável pros cards memoizados, sem furar atualização.
  const presenceOfRef = useRef(presenceOf)
  presenceOfRef.current = presenceOf
  const presenceOfStable = useCallback((id: string | number) => presenceOfRef.current(id), [])
  const now = useTick()
  const operatorServiceConfigs = visibleServiceConfigs ?? serviceConfigs
  const queueSessions = sessionsOf(selServiceConfigId)
  const selectableSessions = queueSessions.filter((s) => matchesPeriod(s, selPeriod))
  const sessionSelectLabel = selPeriod == null ? 'Todas as sessões' : 'Todas as sessões do período'
  const [queuesExpanded, setQueuesExpanded] = useState(false)
  const queueListRef = useRef<HTMLDivElement | null>(null)
  const [queueListMeasured, setQueueListMeasured] = useState(false)
  const [queueListOverflows, setQueueListOverflows] = useState(false)
  // A seta "Mostrar mais filas" só aparece quando o conteúdo REALMENTE estoura o
  // clamp (max-height: 188px). Uma contagem fixa (>5) mostrava a seta e a sombra
  // mesmo com as filas cabendo numa única linha (viewport largo). Pausa a medição
  // enquanto expandido para o botão "Recolher" continuar visível (expande zera o
  // clamp e a medição "não detectaria" overflow). jsdom não faz layout (0×0):
  // cai no fallback por quantidade para manter os testes legíveis.
  useEffect(() => {
    const el = queueListRef.current
    if (!el || queuesExpanded) {return}
    const measure = () => {
      if (el.scrollHeight > 0 && el.clientHeight > 0) {
        setQueueListOverflows(el.scrollHeight > el.clientHeight + 1)
        setQueueListMeasured(true)
      }
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {return}
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [queuesExpanded, operatorServiceConfigs.length])
  const hasHiddenQueues = queueListMeasured ? queueListOverflows : operatorServiceConfigs.length > 5

  // colunas vêm do seam getOperatorWork (mock deriva; real busca operator-work por fila)
  const { serving, called, workNow, blocked, scheduled, confirmed, eligible, priorityCount,
    filteredEligByServiceConfig,
    hasOtherQueuesWithPeople, recallCandidate, primaryCallMode, svcNome } = useMemo(() => {
  const matchesResourceFilter = (row: OperatorRow): boolean => !filterResourceId || !row.t.resourceId || row.t.resourceId === filterResourceId
  const matchesToday = (row: OperatorRow): boolean =>
    row.t.status === 'Called' || row.t.status === 'Started' || isToday(row.t.sessionStart)
  const serving = (work?.serving ?? []).filter((r) => matchesResourceFilter(r) && matchesToday(r))
  // Um bloco só, ordenado: chamado (esperando você iniciar) antes de quem já está em curso.
  // A diferença entre os dois vem do card — trilho, pill e ação primária —, não de outro header:
  // com poucos itens, dois títulos custam mais altura do que entregam em escaneabilidade.
  const called = serving.filter((r) => r.t.status === 'Called')
  const workNow = [...called, ...serving.filter((r) => r.t.status !== 'Called')]
  const rawBlocked = (work?.blocked ?? []).filter((r) =>
    matchesResourceFilter(r) && matchesToday(r) &&
    r.t.serviceConfigId === selServiceConfigId && isPending(r.t.status))
  const confirmedFromBlocked = rawBlocked.filter((r) => r.t.status === 'Confirmed')
  const overdueAsEligible = env.singleDay ? rawBlocked.filter((r) => isWaitingForCall(r) && isOverdueRow(r)) : []
  const baseEligible = (work?.eligible ?? []).filter((r) => matchesResourceFilter(r) && matchesToday(r))
  const scheduled = [...baseEligible, ...rawBlocked].filter((r) => r.t.serviceConfigId === selServiceConfigId && isScheduledRow(r))
  const confirmed = [...baseEligible, ...confirmedFromBlocked]
    .filter((r) => r.t.serviceConfigId === selServiceConfigId && r.t.status === 'Confirmed')
  // singleDay: junta os "atrasados" na fila normal e ordena por horário de sessão —
  // mesmo critério do backoffice legado (o mais antigo primeiro, vencido ou não).
  const eligible = env.singleDay
    ? [...baseEligible.filter(isWaitingForCall), ...overdueAsEligible].sort((a, b) => sessionStartMs(a) - sessionStartMs(b))
    : baseEligible.filter(isWaitingForCall)
  const blocked = env.singleDay ? rawBlocked.filter((r) => !isScheduledRow(r) && r.t.status !== 'Confirmed' && !isOverdueRow(r)) : rawBlocked.filter((r) => !isScheduledRow(r) && r.t.status !== 'Confirmed')
  const priorityCount = eligible.filter((r) => r.a.prioridade > 0).length
  const svc = serviceConfigById(selServiceConfigId)
  const svcNome = svc?.nome ?? 'serviço'
  // Calcula contadores filtrados por data (apenas hoje)
  const filteredEligByServiceConfig: Record<number, number> = {}
  eligible.forEach((r) => {
    const scid = r.t.serviceConfigId ?? 0
    filteredEligByServiceConfig[scid] = (filteredEligByServiceConfig[scid] ?? 0) + 1
  })
  const hasOtherQueuesWithPeople = Object.entries(filteredEligByServiceConfig).some(([id, count]) => Number(id) !== selServiceConfigId && count > 0)
  const calledServing = serving.filter((r) =>
    r.t.status === 'Called' && r.t.serviceConfigId === selServiceConfigId && actionsFor(r.a.tickets, r.t).has('recall'))
  const calledForActingResource = actingResourceId ? calledServing.filter((r) => r.t.resourceId === actingResourceId) : []
  const recallCandidate =
    eligible.length > 0
      ? null
      : calledForActingResource.length === 1
        ? calledForActingResource[0]
        : calledServing.length === 1
          ? calledServing[0]
          : null
  const primaryCallMode: 'next' | 'recall' = recallCandidate ? 'recall' : 'next'
  return { serving, called, workNow, blocked, scheduled, confirmed, eligible, priorityCount,
    filteredEligByServiceConfig,
    hasOtherQueuesWithPeople, recallCandidate, primaryCallMode, svcNome }
  }, [work, filterResourceId, selServiceConfigId, actingResourceId, serviceConfigById])

  const queueCounts = useMemo(() => ({
    ...(selPeriod ? filteredEligByServiceConfig : {
      ...opCounts?.eligCountByServiceConfig,
      ...work?.eligCountByServiceConfig,
    }),
    ...(work && !loading ? { [selServiceConfigId]: filteredEligByServiceConfig[selServiceConfigId] ?? 0 } : {}),
  }), [selPeriod, filteredEligByServiceConfig, opCounts?.eligCountByServiceConfig, work, loading, selServiceConfigId])
  const queueWaitByServiceConfig = useMemo(() => new Map(queueWaits?.map((wait) => [wait.serviceConfigId,
    (queueCounts[wait.serviceConfigId] ?? 0) > 0 ? wait : { ...wait, total: 0 },
  ]) ?? []), [queueWaits, queueCounts])
  const orderedServiceConfigs = useMemo(() => operatorServiceConfigs
    .map((serviceConfig, index) => ({ serviceConfig, index }))
    .sort((a, b) =>
      queueSemaphoreOrder(queueWaitByServiceConfig.get(a.serviceConfig.serviceConfigId), semaphoreLimits?.yellow, semaphoreLimits?.red, now)
      - queueSemaphoreOrder(queueWaitByServiceConfig.get(b.serviceConfig.serviceConfigId), semaphoreLimits?.yellow, semaphoreLimits?.red, now)
      || a.serviceConfig.nome.localeCompare(b.serviceConfig.nome, 'pt-BR')
      || a.index - b.index)
    .map(({ serviceConfig }) => serviceConfig), [operatorServiceConfigs, queueWaitByServiceConfig, semaphoreLimits, now])

  useEffect(() => {
    if (presenceOfWatch) {presenceOfWatch(selServiceConfigId)}
  }, [selServiceConfigId, presenceOfWatch])

  useEffect(() => {
    if (loading) {return}
    if (actingResourceId && !attendants.some((attendant) => attendant.id === actingResourceId)) {
      onActingResource(null)
    }
    if (filterResourceId && !attendants.some((attendant) => attendant.id === filterResourceId)) {
      onFilterResource(null)
    }
  }, [actingResourceId, attendants, filterResourceId, loading, onActingResource, onFilterResource])

  // "Filtrar atendimentos" filtra sempre pelo atendente atual — se trocar de atendente
  // com o filtro marcado, o filtro acompanha em vez de ficar preso no anterior.
  useEffect(() => {
      if (filterResourceId != null && filterResourceId !== actingResourceId) { onFilterResource(actingResourceId) }
    }, [actingResourceId, filterResourceId, onFilterResource])

    // Sentry tags for this screen — clear on unmount/navigation to avoid stale tags
    // between visits (e.g. Detalhes A → Operador → Detalhes A reusing aggregatorId).
    useEffect(() => {
      setTags({
        screen: 'OperadorScreen',
        serviceConfigId: String(selServiceConfigId),
        period: selPeriod ?? 'all',
        actingResourceId: actingResourceId ?? 'none',
        filterResourceId: filterResourceId ?? 'none',
      })
      return () => {
        setTags({
          screen: 'OperadorScreen',
          serviceConfigId: null,
          period: null,
          actingResourceId: null,
          filterResourceId: null,
        })
      }
    }, [selServiceConfigId, selPeriod, actingResourceId, filterResourceId, setTags])

  const queueCountLabel = (scid: number): string => {
    return `${queueCounts[scid] ?? 0} na fila`
  }

  // Atalhos (ação de dezenas/hora, sem tirar a mão do teclado): Enter → próximo, Shift+Enter → preferencial.
  // Refs guardam o estado mais recente; o listener é montado uma vez. O duplo-disparo é barrado na origem
  // (App.callNext ignora reentrada enquanto há chamada em voo).
  const callNextRef = useRef(() => {})
  const callPrefRef = useRef(() => {})
  callNextRef.current = () => {
    if (eligible.length > 0 || recallCandidate) {actions.callNext()}
  }
  callPrefRef.current = () => { actions.callNext(true) }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.repeat || e.metaKey || e.ctrlKey || e.altKey) {return}
      const el = document.activeElement as HTMLElement | null
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || el?.isContentEditable) {return}
      if (!e.shiftKey && (tag === 'BUTTON' || tag === 'A')) {return}
      if (document.querySelector('.fz-overlay')) {return}
      e.preventDefault()
      if (e.shiftKey) {callPrefRef.current()}
      else {callNextRef.current()}
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="fz-page fz-page--operator" data-screen-label="03 Painel do operador">
      <div className="fz-page__head">
        <div>
          <h1 className="fz-page__title">Painel do operador · Hoje</h1>
          <div className="fz-page__sub">Mostrando apenas {terms.subjectPluralLower} agendados para hoje — selecione sua fila</div>
        </div>
      </div>

      <div className="fz-opconfig">
        <div className="fz-opconfig__row">
          <div className="fz-opconfig__group fz-opconfig__group--queues">
            <span className="fz-opconfig__label">Fila selecionada</span>
            <div className={'fz-roompick-frame ' + (hasHiddenQueues ? 'has-more ' : '') + (queuesExpanded ? 'is-expanded' : '')}>
              <div className="fz-roompick fz-roompick--clamped" style={{ marginBottom: 0 }} ref={queueListRef}>
                {orderedServiceConfigs.map((s) => {
                  const color = queueSemaphore(queueWaitByServiceConfig.get(s.serviceConfigId), semaphoreLimits?.yellow, semaphoreLimits?.red, now)
                  const statusLabel = color === 'green' ? 'Fila no prazo' : color === 'yellow' ? 'Fila em atenção' : color === 'red' ? 'Fila atrasada' : undefined
                  return <button key={s.serviceConfigId} className={`fz-room${color ? ` is-${color}` : ''}${selServiceConfigId === s.serviceConfigId ? ' is-active' : ''}`} aria-pressed={selServiceConfigId === s.serviceConfigId} aria-description={statusLabel} onClick={() => onSelServiceConfig(s.serviceConfigId)}>
                    <div className="fz-room__name"><span className="fz-room__code">{s.code}</span> {s.nome}</div>
                    <div className="fz-room__sala">{s.sala}</div>
                    <div className="fz-room__count">
                      <span>{queueCountLabel(s.serviceConfigId)}</span>
                      {color && <span className="fz-room__signal" aria-hidden="true" />}
                    </div>
                  </button>
                })}
                {operatorServiceConfigs.length === 0 && (
                  <div className="fz-opempty"><Icon name="lock" size={17} /><span>Nenhuma fila pronta para atendimento.</span></div>
                )}
              </div>
              {hasHiddenQueues && (
                <button
                  type="button"
                  className="fz-roompick__more"
                  aria-expanded={queuesExpanded}
                  aria-label={queuesExpanded ? 'Recolher filas' : 'Mostrar mais filas'}
                  onClick={() => setQueuesExpanded((expanded) => !expanded)}
                >
                  <Icon name="chevD" size={13} className="fz-roompick__more-icon" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="fz-opconfig__row fz-opconfig__row--split">
          <div className="fz-opconfig__group">
            <div className="fz-sesspick" style={{ marginBottom: 0 }}>
              <span className="fz-sesspick__label">Período</span>
              <button className={'fz-chip ' + (selPeriod === null && selSessionId === null ? 'is-active' : '')} aria-pressed={selPeriod === null && selSessionId === null} onClick={() => { onSelPeriod(null); onSelSession(null) }}>
                Todas
              </button>
              {PERIOD_OPTIONS.map((period) => (
                <button key={period.value} className={'fz-chip ' + (selPeriod === period.value ? 'is-active' : '')} aria-pressed={selPeriod === period.value} onClick={() => { onSelPeriod(period.value); onSelSession(null) }}>
                  {period.label}
                </button>
              ))}
              {queueSessions.length > 0 && (
                <Select
                  className="fz-sesspick__select"
                  ariaLabel="Sessão específica"
                  compact
                  value={selSessionId == null ? '' : String(selSessionId)}
                  onChange={(v) => onSelSession(v === '' ? null : Number(v))}
                  options={[
                    { value: '', label: sessionSelectLabel },
                    ...selectableSessions.map((session) => {
                      const count = work?.eligCountBySession?.[selServiceConfigId]?.[session.sessionId] ?? 0
                      return { value: String(session.sessionId), label: sessionOptionLabel(session, count) }
                    }),
                  ]}
                />
              )}
            </div>
          </div>
          <div className="fz-opheader">
            <label className="fz-opheader__field">
              <span>Atendendo como</span>
              <AttendantSelect attendants={attendants} value={actingResourceId} onChange={onActingResource} compact />
            </label>
            <label className="fz-toggle">
              <input
                type="checkbox"
                checked={filterResourceId != null}
                onChange={(e) => onFilterResource(e.target.checked ? actingResourceId : null)}
              />
              <span className="fz-toggle__track" />
              Filtrar atendimentos
            </label>
          </div>
        </div>
      </div>

      <div className="fz-callbar">
        <button
          className="fz-callbar__main"
          disabled={(eligible.length === 0 && !recallCandidate) || calling !== null}
          onClick={() => actions.callNext()}
        >
          <span className="fz-callbar__lead">
            {calling === 'next' ? <span className="fz-spin" /> : <Icon name="megaphone" size={22} />}
            <span className="fz-callbar__txt">
              <span className="fz-callbar__title">
                Chamar próximo <kbd className="fz-callbar__kbd">↵</kbd>
              </span>
              <span className="fz-callbar__ctx">
                {primaryCallMode === 'recall' && recallCandidate
                  ? `${recallCandidate.t.codigoTicket} · aplicar regra da fila`
                  : `${svcNome} · por ordem da fila`}
              </span>
            </span>
          </span>
          <span className="fz-callbar__queue">
            <span className="fz-callbar__num" aria-live="polite" aria-atomic="true">{primaryCallMode === 'recall' ? 1 : eligible.length}</span>
            <span className="fz-callbar__unit">{primaryCallMode === 'recall' ? 'em chamada' : 'na fila'}</span>
          </span>
        </button>
        <button className="fz-callbar__pref" disabled={priorityCount === 0 || calling !== null} onClick={() => actions.callNext(true)} title="Chamar próximo preferencial (Shift+Enter)">
          {calling === 'pref' ? <span className="fz-spin" /> : <Icon name="star" size={18} />}
          <span className="fz-callbar__preflabel">Preferencial</span>
          <span className="fz-callbar__prefnum">{priorityCount}</span>
        </button>
      </div>
      <div className="fz-callbar__hint">
        <kbd>Enter</kbd> chama o próximo · <kbd>Shift</kbd>+<kbd>Enter</kbd> o preferencial
      </div>

      <Legend id="operador">
        A fila mostra só {terms.subjectPluralLower} liberados para esta fila. Serviços do mesmo nível são atendidos <b>um por vez</b>; os bloqueados aguardam a etapa anterior ou um concorrente do nível terminar.
      </Legend>

      {opLayout === 'kanban' ? (
        <div className="fz-opboard">
          <div className="fz-opcol fz-opcol--serving">
            <div className="fz-opcol__head">
              <span className="fz-opcol__title">
                Atendendo agora
                {called.length > 0 && <span className="fz-opcol__sublabel">{called.length} aguardando iniciar</span>}
              </span>
              <span className="fz-opcol__count">{workNow.length}</span>
            </div>
            <div className="fz-opcol__list">
              {workNow.length === 0 && (loading
                ? <RowSkeleton count={3} ariaLabel="Carregando atendimentos" />
                : <div className="fz-opempty"><Icon name="stop" size={17} /><span>Nada em atendimento. Chame o próximo de {svcNome.toLowerCase()}.</span></div>)}
              {workNow.map((r) => <ServingCard key={r.t.ticketId} row={r} actions={actions} actingResourceId={actingResourceId} />)}
            </div>
          </div>
          <div className="fz-opcol fz-opcol--queue">
            <div className="fz-opcol__head"><span className="fz-opcol__title">Fila — pode chamar</span><span className="fz-opcol__count">{eligible.length}</span></div>
            <div className="fz-opcol__list">
              {eligible.length === 0 && (loading
                ? <RowSkeleton count={6} ariaLabel="Carregando fila" />
                : <div className="fz-opempty"><Icon name="clock" size={17} /><span>{hasOtherQueuesWithPeople ? 'Ninguém liberado para esta fila agora, mas há pessoas aguardando em outras filas. Considere trocar de fila acima.' : 'Ninguém liberado para esta fila agora.'}</span></div>)}
              {eligible.map((r, i) => <EligCard key={r.t.ticketId} row={r} i={i} actions={actions} presenceOf={presenceOfStable} />)}
            </div>
            {confirmed.length > 0 && <>
              <div className="fz-opcol__head fz-opcol__head--blocked"><span className="fz-opcol__title fz-opcol__title--muted">Confirmados</span><span className="fz-opcol__count">{confirmed.length}</span></div>
              <div className="fz-opcol__list">
                {confirmed.map((r, i) => <PreCheckinCard key={r.t.ticketId} row={r} i={i} actions={actions} presenceOf={presenceOfStable} />)}
              </div>
            </>}
            {scheduled.length > 0 && <>
              <div className="fz-opcol__head fz-opcol__head--blocked"><span className="fz-opcol__title fz-opcol__title--muted">Agendados</span><span className="fz-opcol__count">{scheduled.length}</span></div>
              <div className="fz-opcol__list">
                {scheduled.map((r, i) => <PreCheckinCard key={r.t.ticketId} row={r} i={i} actions={actions} presenceOf={presenceOfStable} />)}
              </div>
            </>}
            {blocked.length > 0 && <>
              <div className="fz-opcol__head fz-opcol__head--blocked"><span className="fz-opcol__title fz-opcol__title--muted">Bloqueados</span><span className="fz-opcol__count">{blocked.length}</span></div>
              <div className="fz-opcol__list">
                {blocked.map((r) => <BlockedCard key={r.t.ticketId} row={r} actions={actions} />)}
              </div>
            </>}
          </div>
        </div>
      ) : (
        <div className="fz-queue">
          <div className="fz-queue__controls">
            <span className="fz-opcol__title">
              Atendendo agora
              {called.length > 0 && <span className="fz-opcol__sublabel">{called.length} aguardando iniciar</span>}
            </span>
            <span className="fz-opcol__count">{workNow.length}</span>
          </div>
          {workNow.map((r) => <ServingCard key={r.t.ticketId} row={r} actions={actions} actingResourceId={actingResourceId} />)}
          <div className="fz-opcol__head"><span className="fz-opcol__title">Fila — pode chamar</span><span className="fz-opcol__count">{eligible.length}</span></div>
          {eligible.length === 0 && (loading
            ? <RowSkeleton count={6} ariaLabel="Carregando fila" />
            : <div className="fz-opempty"><Icon name="clock" size={17} /><span>{hasOtherQueuesWithPeople ? 'Ninguém liberado para esta fila agora, mas há pessoas aguardando em outras filas. Considere trocar de fila acima.' : 'Ninguém liberado para esta fila agora.'}</span></div>)}
          {eligible.map((r, i) => <EligCard key={r.t.ticketId} row={r} i={i} actions={actions} presenceOf={presenceOfStable} />)}
          {confirmed.length > 0 && <>
            <div className="fz-opcol__head fz-opcol__head--blocked"><span className="fz-opcol__title fz-opcol__title--muted">Confirmados</span><span className="fz-opcol__count">{confirmed.length}</span></div>
            {confirmed.map((r, i) => <PreCheckinCard key={r.t.ticketId} row={r} i={i} actions={actions} presenceOf={presenceOfStable} />)}
          </>}
          {scheduled.length > 0 && <>
            <div className="fz-opcol__head fz-opcol__head--blocked"><span className="fz-opcol__title fz-opcol__title--muted">Agendados</span><span className="fz-opcol__count">{scheduled.length}</span></div>
            {scheduled.map((r, i) => <PreCheckinCard key={r.t.ticketId} row={r} i={i} actions={actions} presenceOf={presenceOfStable} />)}
          </>}
          {blocked.length > 0 && <>
            <div className="fz-opcol__head fz-opcol__head--blocked"><span className="fz-opcol__title fz-opcol__title--muted">Atrasados</span><span className="fz-opcol__count">{blocked.length}</span></div>
            {blocked.map((r) => <BlockedCard key={r.t.ticketId} row={r} actions={actions} />)}
          </>}
        </div>
      )}
    </div>
  )
}
